///
/// EarthBrowser
///
/// Precipitation Flow Texture Series
///
/// A RadarTextureSeries that estimates frame-to-frame optical flow for the
/// current blend pair so the overlay shader can advect precipitation toward
/// the next frame instead of cross-dissolving. Flow is computed in a worker
/// from the cached frame images (already in the sqlite image cache after
/// the frame textures load) and attached to the current frame as a small
/// RG32F texture, mirroring the synoptic isobar flow pipeline.
///
/// Created by Matt Giger on 9/14/2026
/// Copyright (c) 2026 Geodata Labs
///

import { wrap, type Remote } from "comlink"
import PrecipFlowWorker from "@/workers/precip_flow/index.ts?worker"
import type { PrecipFlowWorkerType } from "@/workers/precip_flow/index"
import { CacheTable } from "@/earthbrowser/database/tables/CacheTable"
import { RadarTextureSeries } from "@/earthbrowser/dataset-distribution/mrms/MrmsOverlay"
import { type RadarTextureFrame } from "@/earthbrowser/dataset-distribution/mrms/RadarTextureFrame"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { type RenderState } from "@stovokor/engine/RenderState"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { Texture } from "@stovokor/shader/Texture"

// One worker shared across series instances; flow requests are infrequent
// (one per frame-pair transition)
let flowWorkerRef: Remote<PrecipFlowWorkerType> | undefined
function getFlowWorker(): Remote<PrecipFlowWorkerType> {
	if (!flowWorkerRef) {
		flowWorkerRef = wrap<PrecipFlowWorkerType>(new PrecipFlowWorker())
	}
	return flowWorkerRef
}

export class PrecipFlowTextureSeries extends RadarTextureSeries<RadarTextureFrame> {
	private ebContext: EarthBrowserContext
	private computingKey?: string
	// Pairs whose estimation failed permanently (decode error, size mismatch);
	// bounded by the frame window, cleared with the frames
	private failedKeys = new Set<string>()

	constructor(context: EarthBrowserContext) {
		super(context)
		this.ebContext = context
	}

	/** Flow texture valid for the current blend pair, or the zero-flow blank.
	 *  When texture1 has fallen back to texture0 (next frame not loaded yet)
	 *  a warp would double-image the field, so return zero flow. */
	public get flowTexture(): Texture {
		return this.pairFlowTexture ?? this.ebContext.blankTexture
	}

	/** The estimated flow for the current blend pair once it is ready, else
	 *  undefined. Consumers that blend the same frame pair through another
	 *  path (the radar refinement tiles) sample this field in equirect UV */
	public get pairFlowTexture(): Texture | undefined {
		const cur = this.currentFrame
		const next = this.nextFrame
		if (cur?.flowTexture?.available && next
			&& cur.flowTargetUrl === next.url
			&& this.texture1 !== this.texture0) {
			return cur.flowTexture
		}
		return undefined
	}

	public override updateState(state: RenderState): void {
		super.updateState(state)
		this.updateFlow()
	}

	public override clear(): void {
		super.clear()
		this.failedKeys.clear()
	}

	private updateFlow(): void {
		const cur = this.currentFrame
		const next = this.nextFrame
		if (!cur || !next || cur === next || !cur.available || !next.available) {
			return
		}
		if (cur.flowTexture && cur.flowTargetUrl === next.url) {
			return
		}
		const key = `${cur.url}|${next.url}`
		if (this.computingKey !== undefined || this.failedKeys.has(key)) {
			return
		}
		this.computingKey = key
		void this.computeFlow(cur, next, key)
	}

	private async computeFlow(cur: RadarTextureFrame, next: RadarTextureFrame, key: string): Promise<void> {
		try {
			// The frame textures loaded through ImageLoader, so the encoded
			// images are in the sqlite cache. A miss can only be a not-yet-
			// committed cache write; leave the pair unmarked and retry later
			const [image0, image1] = await Promise.all([CacheTable.get(cur.url), CacheTable.get(next.url)])
			if (!image0 || !image1) {
				return
			}

			const flow = await getFlowWorker().estimate(image0, image1)
			if (!flow) {
				this.failedKeys.add(key)
				return
			}
			if (!cur.available) {
				return	// frame was purged while computing
			}

			// Float textures default to NEAREST; linear filtering of floats
			// needs the (near universally supported) extension
			const linear = this.ebContext.hasExtension('OES_texture_float_linear')
			const filter = linear ? TextureFilter.Linear : TextureFilter.Nearest
			const tex = new Texture(this.ebContext)
			tex.minFilter = filter
			tex.magFilter = filter
			tex.wrapS = TextureWrap.Repeat
			tex.wrapT = TextureWrap.ClampToEdge
			tex.uploadRawData(flow.data, flow.width, flow.height, InternalFormat.RG32F, PixelFormat.RG, ComponentType.Float)

			cur.flowTexture?.destroy()
			cur.flowTexture = tex
			cur.flowTargetUrl = next.url
			console.log(`[PrecipFlowTextureSeries] flow ready ${flow.width}x${flow.height} for ${key.slice(key.lastIndexOf('/') + 1)}`)
		} catch (e) {
			this.failedKeys.add(key)
			console.warn('[PrecipFlowTextureSeries] flow computation failed', e)
		} finally {
			this.computingKey = undefined
		}
	}
}
